import { Fragment, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useQueryClient } from '@tanstack/react-query';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ASSISTANT_LIMIT_DEFAULTS, isRateableMessage, type ChatCitation } from '@hv/shared';
import { describeError, useNotify } from '../components/Notify';
import { BRAND } from '../theme';
import { ChatBubble } from './ChatBubble';
import { ChatErrorBubble } from './ChatErrorBubble';
import { ChatFeedback } from './ChatFeedback';
import { ChatComposer } from './ChatComposer';
import { ChatLimitNotice, chatBlock } from './ChatLimits';
import { assistantKeys, isLimitError, useAskChat, useChat, useDeleteChat, useNewChat, usePendingQuestion, usePendingRetry, useRetryChat } from './hooks';
import { rescoreDividers } from './rescore';
import { SuggestedQuestions } from './SuggestedQuestions';
import { TypingIndicator } from './TypingIndicator';

export interface AssistantPanelProps {
  householdId: string;
  familyName: string | null;
  fullScreen: boolean;
  onClose: () => void;
  onOpenHousehold: (householdId: string) => void;
  onCite: (c: ChatCitation) => void;
}

export function AssistantPanel({ householdId, familyName, fullScreen, onClose, onOpenHousehold, onCite }: AssistantPanelProps) {
  const chat = useChat(householdId);
  const ask = useAskChat();
  const newChat = useNewChat();
  const deleteChat = useDeleteChat();
  const retry = useRetryChat();
  const { notify, notifyError } = useNotify();
  const [draft, setDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const messages = chat.data?.messages ?? [];
  const thread = chat.data?.thread ?? null;
  const pending = usePendingQuestion(householdId);
  const retrying = usePendingRetry(householdId);
  const busy = pending !== null || retrying !== null || newChat.isPending || deleteChat.isPending;
  const mergedInto = chat.data?.mergedInto ?? null;
  const dividers = rescoreDividers(messages);
  const qc = useQueryClient();
  const limits = chat.data?.limits;
  const block = chatBlock(limits);
  const resetsAt = block?.kind === 'rate' ? block.usage.resetsAt : null;

  useEffect(() => {
    if (!resetsAt) return;
    const timer = setTimeout(() => void qc.invalidateQueries({ queryKey: assistantKeys.chat(householdId) }), Math.max(0, new Date(resetsAt).getTime() - Date.now()) + 1_000);
    return () => clearTimeout(timer);
  }, [resetsAt, householdId, qc]);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  const send = (raw: string) => {
    const text = raw.trim();
    if (!text || busy || block) return;
    setDraft('');
    ask.mutate({ householdId, text }, { onError: () => setDraft(text) });
  };

  const startNewChat = () =>
    newChat.mutate(householdId, {
      onSuccess: () => {
        ask.reset();
        retry.reset();
      },
      onError: (err) => notifyError(err, 'Starting a new chat failed'),
    });

  const removeChat = () =>
    thread &&
    deleteChat.mutate(thread.id, {
      onSuccess: () => {
        ask.reset();
        retry.reset();
        setConfirmDelete(false);
        notify('success', 'Conversation deleted');
      },
      onError: (err) => notifyError(err, 'Deleting the conversation failed'),
    });

  return (
    <Box
      component="aside"
      aria-label="Ask Loom"
      sx={{ width: fullScreen ? '100%' : 400, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fbfcfe', borderRight: fullScreen ? 'none' : `1px solid ${BRAND.line}` }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: `1px solid ${BRAND.line}` }}>
        <Box sx={{ minWidth: 0 }}>
          {fullScreen && (
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={onClose} sx={{ ml: -1, mb: 0.5 }}>
              Back to details
            </Button>
          )}
          <Typography variant="h3" component="h2">
            Ask Loom{familyName ? ` · ${familyName}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Answers use only this household's record. Check important facts in the details.
          </Typography>
        </Box>
        <Stack direction="row" flexShrink={0}>
          <Tooltip title="New chat">
            <span>
              <IconButton aria-label="New chat" onClick={startNewChat} disabled={busy || !!mergedInto || messages.length === 0}>
                <AddCommentOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete chat">
            <span>
              <IconButton aria-label="Delete chat" onClick={() => setConfirmDelete(true)} disabled={busy || !thread}>
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
          {!fullScreen && (
            <IconButton aria-label="Close Ask Loom" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {chat.data?.stale && (
        <Alert severity="warning" data-testid="chat-stale" sx={{ borderRadius: 0 }}>
          Inputs changed since this score was produced.
        </Alert>
      )}
      {mergedInto && (
        <Alert
          severity="info"
          data-testid="chat-merged"
          sx={{ borderRadius: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => onOpenHousehold(mergedInto.id)}>
              Open {mergedInto.familyLastName}
            </Button>
          }
        >
          This household was merged into {mergedInto.familyLastName}. This conversation is read-only.
        </Alert>
      )}
      <Box ref={scroller} role="log" aria-label="Conversation" sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {chat.isPending && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={24} aria-label="Loading conversation" />
          </Box>
        )}
        {chat.error && <Alert severity="error">Could not load the conversation: {describeError(chat.error)}</Alert>}
        {chat.data && !mergedInto && messages.length === 0 && !pending && <SuggestedQuestions onPick={send} disabled={busy || !!block} />}
        {messages.map((m) => (
          <Fragment key={m.id}>
            {dividers.has(m.id) && (
              <Divider data-testid="rescore-divider" sx={{ my: 1.5, typography: 'caption', color: 'text.secondary' }}>
                {dividers.get(m.id)}
              </Divider>
            )}
            {m.role === 'assistant' && m.status === 'error' ? (
              <ChatErrorBubble text={m.text} retrying={retrying === m.id} disabled={busy || !!mergedInto || block?.kind === 'rate'} onRetry={() => retry.mutate({ householdId, messageId: m.id })} />
            ) : (
              <ChatBubble role={m.role} text={m.text} status={m.status} unverifiedSpans={m.unverifiedSpans} citations={m.citations} onCite={onCite} />
            )}
            {isRateableMessage(m) && <ChatFeedback messageId={m.id} rating={m.rating} />}
          </Fragment>
        ))}
        {pending && <ChatBubble role="user" text={pending} />}
        {pending && <TypingIndicator />}
        {ask.error && !pending && !isLimitError(ask.error) && <Alert severity="error">{describeError(ask.error)}</Alert>}
        {retry.error && !retrying && !isLimitError(retry.error) && <Alert severity="error">{describeError(retry.error)}</Alert>}
      </Box>
      {!mergedInto && limits && (
        <Box sx={{ px: 1.5, pt: 1 }}>
          <ChatLimitNotice limits={limits} block={block} onNewChat={startNewChat} newChatPending={newChat.isPending} />
        </Box>
      )}
      {!mergedInto && (
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSend={() => send(draft)}
          disabled={busy || !chat.data || !!block}
          maxChars={limits?.questionMaxChars ?? ASSISTANT_LIMIT_DEFAULTS.questionMaxChars}
        />
      )}
      <Dialog open={confirmDelete} onClose={() => !deleteChat.isPending && setConfirmDelete(false)}>
        <DialogTitle>Delete this conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your conversation about {familyName ?? 'this household'} will be permanently deleted. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleteChat.isPending}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={removeChat} disabled={deleteChat.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
